import { useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd, { buildArticleSchema, buildBreadcrumbSchema } from '@/components/feature/JsonLd';
import { getArticleBySlug, blogArticles } from '@/mocks/articles';

function renderContent(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let currentList: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let key = 0;

  const flushList = () => {
    if (currentList.length === 0 || !listType) return;
    const Tag = listType === 'ul' ? 'ul' : 'ol';
    elements.push(
      <Tag key={`list-${key++}`} className={`${listType === 'ul' ? 'list-disc' : 'list-decimal'} pl-6 space-y-2 my-4`}>
        {currentList.map((item, i) => (
          <li key={i} className="text-foreground-700 leading-relaxed">
            {renderInline(item.replace(/^[-\d.]+\s+/, ''))}
          </li>
        ))}
      </Tag>
    );
    currentList = [];
    listType = null;
  };

  const renderInline = (str: string) => {
    const parts: React.ReactNode[] = [];
    let remaining = str;
    let idx = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

      let firstMatch: { type: 'bold' | 'link'; index: number; text: string; href?: string } | null = null;

      if (boldMatch && (!firstMatch || boldMatch.index < firstMatch.index)) {
        firstMatch = { type: 'bold', index: boldMatch.index, text: boldMatch[1] };
      }
      if (linkMatch && (!firstMatch || linkMatch.index < firstMatch.index)) {
        firstMatch = { type: 'link', index: linkMatch.index, text: linkMatch[1], href: linkMatch[2] };
      }

      if (!firstMatch || firstMatch.index === -1) {
        parts.push(<span key={idx++}>{remaining}</span>);
        break;
      }

      if (firstMatch.index > 0) {
        parts.push(<span key={idx++}>{remaining.slice(0, firstMatch.index)}</span>);
      }

      if (firstMatch.type === 'bold') {
        parts.push(<strong key={idx++} className="text-foreground-950">{firstMatch.text}</strong>);
      } else if (firstMatch.type === 'link' && firstMatch.href) {
        if (firstMatch.href.startsWith('http')) {
          parts.push(
            <a key={idx++} href={firstMatch.href} target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium underline">
              {firstMatch.text}
            </a>
          );
        } else {
          parts.push(
            <Link key={idx++} to={firstMatch.href} className="text-primary-600 hover:text-primary-700 font-medium underline">
              {firstMatch.text}
            </Link>
          );
        }
      }

      remaining = remaining.slice(firstMatch.index + (firstMatch.type === 'bold' ? boldMatch![0].length : linkMatch![0].length));
    }

    return parts;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={key++} className="font-heading text-2xl font-bold text-foreground-950 pt-6 pb-2">
          {trimmed.slice(3)}
        </h2>
      );
      return;
    }

    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={key++} className="font-heading text-xl font-semibold text-foreground-950 pt-4 pb-1">
          {trimmed.slice(4)}
        </h3>
      );
      return;
    }

    if (trimmed.startsWith('- ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(trimmed);
      return;
    }

    if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(trimmed);
      return;
    }

    flushList();
    elements.push(
      <p key={key++} className="text-foreground-700 leading-relaxed text-base">
        {renderInline(trimmed)}
      </p>
    );
  });

  flushList();
  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = useMemo(() => {
    if (!slug) return undefined;
    return getArticleBySlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-28 pb-16 px-6 md:px-10 max-w-[800px] mx-auto text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-500 mb-6">The article you are looking for does not exist.</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  const articleSchema = buildArticleSchema(
    article.title,
    article.excerpt,
    article.image,
    article.date,
    `/${article.slug}`,
  );

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: article.title, path: `/${article.slug}` },
  ]);

  const relatedArticles = blogArticles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />

      <article className="pt-28 pb-16">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <nav className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-500 text-sm hover:text-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.15] mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-foreground-400 text-sm flex-wrap">
              <span>{article.date}</span>
              <span>&bull;</span>
              <span>{article.readTime}</span>
              <span>&bull;</span>
              <span>By pandabuy.wiki Editorial Team</span>
            </div>
          </header>

          <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-10 bg-background-200">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom text-foreground-700 leading-relaxed space-y-4">
            {renderContent(article.content)}
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200/70">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/${ra.slug}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="block rounded-xl overflow-hidden bg-background-100 group"
                  >
                    <div className="h-[120px] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-sm text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <Footer />
    </main>
  );
}